// Drop Zone v2 — node-only folder scanner (spec #761, §B.2 Layer 2).
//
// This is the ONLY file in the detection engine that touches the filesystem.
// Everything else operates on a plain `DropEntry[]`, so the core stays
// isomorphic — a browser preview (Phase 2) imports `detectFromEntries` from
// `core.ts` and never pulls `node:fs` into its bundle. The CLI/server use
// `detectArtifact` with a `folder` source, which routes through here.

import { readdirSync, statSync, readFileSync } from 'node:fs';
import { join } from 'node:path';

import type { DropEntry } from './types.js';
import type { PackageJsonShape, RepoSnapshot } from './knowledge-base.js';
import { isNoiseSegment } from './noise.js';
import { packageJsonHasNext } from './heuristics/app.js';

/**
 * Walk a folder to the top level plus one level deep (the spec's inspection
 * depth), returning every non-noise file as a `DropEntry` with a path relative
 * to `folderPath`. Best-effort: an unreadable subdirectory is skipped, never
 * thrown — detection should degrade, not crash.
 */
export function scanFolder(folderPath: string): DropEntry[] {
  const out: DropEntry[] = [];

  let topLevel: string[];
  try {
    topLevel = readdirSync(folderPath);
  } catch {
    return out;
  }

  for (const name of topLevel) {
    if (isNoiseSegment(name)) continue;
    const abs = join(folderPath, name);
    let st;
    try {
      st = statSync(abs);
    } catch {
      continue;
    }
    if (st.isFile()) {
      out.push({ path: name, size: st.size });
      continue;
    }
    if (!st.isDirectory()) continue;

    let children: string[];
    try {
      children = readdirSync(abs);
    } catch {
      continue;
    }
    for (const child of children) {
      if (isNoiseSegment(child)) continue;
      const childAbs = join(abs, child);
      try {
        const cst = statSync(childAbs);
        if (cst.isFile()) {
          out.push({ path: `${name}/${child}`, size: cst.size });
        }
      } catch {
        // Skip unreadable child.
      }
    }
  }

  return out;
}

/**
 * Layer-2 content read: does a `package.json` at the top level or one level
 * deep declare a `next` dependency? Best-effort and silent on any failure.
 */
export function folderHasNextDependency(folderPath: string, files: DropEntry[]): boolean {
  const candidates = files.filter((f) => {
    const base = f.path.slice(f.path.lastIndexOf('/') + 1).toLowerCase();
    return base === 'package.json';
  });
  for (const f of candidates) {
    try {
      const raw = readFileSync(join(folderPath, f.path), 'utf-8');
      if (packageJsonHasNext(JSON.parse(raw))) return true;
    } catch {
      // Unreadable or unparseable — ignore.
    }
  }
  return false;
}

/** Files at depth ≤ 2 whose basename (case-insensitive) equals `basename`, shallowest first. */
function manifestCandidates(files: DropEntry[], basename: string): DropEntry[] {
  const wanted = basename.toLowerCase();
  return files
    .filter((f) => f.path.slice(f.path.lastIndexOf('/') + 1).toLowerCase() === wanted)
    .sort((a, b) => a.path.split('/').length - b.path.split('/').length);
}

/** Best-effort read + parse of the shallowest `package.json`. Null on any failure. */
function readPackageJson(folderPath: string, files: DropEntry[]): PackageJsonShape | null {
  for (const f of manifestCandidates(files, 'package.json')) {
    try {
      const parsed = JSON.parse(readFileSync(join(folderPath, f.path), 'utf-8'));
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as PackageJsonShape;
      }
    } catch {
      // Unreadable or unparseable — try the next candidate.
    }
  }
  return null;
}

/** Best-effort read of the shallowest `requirements.txt` contents. Null on any failure. */
function readRequirements(folderPath: string, files: DropEntry[]): string | null {
  for (const f of manifestCandidates(files, 'requirements.txt')) {
    try {
      return readFileSync(join(folderPath, f.path), 'utf-8');
    } catch {
      // Unreadable — try the next candidate.
    }
  }
  return null;
}

/**
 * Build the knowledge-base `RepoSnapshot` from a scanned folder: the file list
 * plus best-effort reads of `package.json` and `requirements.txt` contents.
 * Node-only (it touches `node:fs`); the matcher it feeds stays pure.
 */
export function readRepoSnapshot(folderPath: string, files: DropEntry[]): RepoSnapshot {
  return {
    files: files.map((f) => f.path),
    package_json: readPackageJson(folderPath, files),
    requirements_txt: readRequirements(folderPath, files),
  };
}
